import { type QueryResult } from '@apollo/client'
import { useDeepMemo } from '@retailer-platform/shared-common/src/react/react.hooks'
import localforage from 'localforage'
import { isEqual } from 'lodash-es'
import { useEffect } from 'react'
import { type ApolloError, type QueryHookOptions } from '@apollo/client'
import { useLocalForageKeyDirect } from '../local-storage/useLocalForageKeyDirect.hooks'

export type QueryHook<TData, TVariables> = (
  options?: QueryHookOptions<TData, TVariables>
) => QueryResult<TData, TVariables>

/**
 * Helper type to extract TData from a query hook
 */
type ExtractQueryData<T> = T extends (options?: QueryHookOptions<infer TData, any>) => any
  ? TData
  : never

// Cache is namespaced, in case we need to fully clear it without touching other keys
const cacheNamespace = 'staleWhileRevalidate'
// This can be useful to invalidate cache -- recommended to bump if underlying data changes
const cacheVersion = 'v1'
// Maximum size for a single cache entry (5MB) - prevents large responses from filling IndexedDB
const MAX_CACHE_ENTRY_BYTES = 5 * 1024 * 1024
// Default maximum age for cache entries (3 days) - entries older than this are treated as expired
const DEFAULT_MAX_CACHE_AGE_MS = 3 * 24 * 60 * 60 * 1000

/**
 * Wrapper type for cached data that includes timestamp for TTL checking
 */
type CachedEntry<TData> = {
  data: TData
  cachedAt: number
}

export type StaleWhileRevalidateOptions = {
  baseCacheKey: string
  /**
   * Maximum age for cache entries in milliseconds.
   * Entries older than this are treated as expired.
   * @default 3 days (259200000ms)
   */
  maxCacheAgeMs?: number
}

/**
 * This will receive a query hook and add localstorage caching functionality to it.
 * Note that this will still call the underlying resource, and automatically update the cache once it gets
 * the new information.
 *
 * This will change the return slightly based on whether the item is in cache or not. It introduces a new key called
 * `errorWhileRevalidating` that will contain the contents of `error` when the item is in cache.
 *
 * This allows the consumer to do something different (e.g. showing a stale data warning) instead of fully erroring out.
 * For more details on how the return changes, refer to the following table:
 *
 * | in cache                     | data                 | loading  | error     | errorWhileRevalidating | ...rest  |
 * |------------------------------|----------------------|----------|-----------|------------------------|----------|
 * | unknown (loading cache data) | undefined            | true     | undefined | undefined              | original |
 * | yes                          | original || cached   | false    | undefined | original               | original |
 * | no (or skipCache: true)      | original             | original | original  | undefined              | original |
 *
 * @param useQueryHook
 * @param options
 * @returns
 */
export const makeQueryStaleWhileRevalidate =
  <THook extends QueryHook<any, any>>(useQueryHook: THook, options: StaleWhileRevalidateOptions) =>
  (
    queryOptions: Parameters<THook>[0],
    cacheOptions?: {
      skipCache?: boolean
    }
  ): ReturnType<THook> & {
    /**
     * Will contain the error from this request as long as there was an error and
     * this request was cached. This is meant so the normal error can be 'swallowed'
     * while still allowing us to act on it.
     */
    errorWhileRevalidating?: ApolloError
  } => {
    type TData = ExtractQueryData<THook>
    type THookResult = ReturnType<THook> & { errorWhileRevalidating?: ApolloError }

    const cacheKey = useDeepMemo(
      () =>
        `${cacheNamespace}:${cacheVersion}:${options.baseCacheKey}:${JSON.stringify(
          queryOptions.variables
        )}`,
      // @ts-ignore, types for useDeepMemo are not right
      [queryOptions.variables]
    )

    const result = useQueryHook(queryOptions)

    // If Apollo already has data (cache hit), defer IndexedDB load to requestIdleCallback
    // for housekeeping (cache updates, staleness checks). This avoids blocking the main
    // thread when the data is already available from Apollo's cache.
    const {
      data: cachedEntry,
      loading: cacheLoading,
      setData,
      hasKey,
    } = useLocalForageKeyDirect<CachedEntry<TData>>(cacheKey, {
      deferLoad: !!result.data,
    })

    // Check if cached entry is expired
    const maxCacheAgeMs = options.maxCacheAgeMs ?? DEFAULT_MAX_CACHE_AGE_MS
    const isExpired = cachedEntry && Date.now() - cachedEntry.cachedAt > maxCacheAgeMs
    const cachedData = isExpired ? undefined : cachedEntry?.data
    const hasValidCache = !isExpired && hasKey

    // Persist data to cache
    // Note that cached items follow an LRU eviction policy by default
    // https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Browser_storage_limits_and_eviction_criteria#lru_policy
    useEffect(() => {
      // Wait for cache to load before writing, so we can compare against existing cached data
      // This prevents unnecessary writes when Apollo returns the same data as cached
      if (!result.error && result.data && !cacheLoading) {
        // Skip if cache is valid and data hasn't changed (avoids redundant serialization and write)
        // Note: if expired, we still write to refresh cachedAt timestamp
        if (!isExpired && cachedEntry?.data && isEqual(cachedEntry.data, result.data)) {
          return
        }

        // Check size before caching to prevent large responses from filling IndexedDB
        const serialized = JSON.stringify(result.data)
        const sizeBytes = new Blob([serialized]).size

        if (sizeBytes <= MAX_CACHE_ENTRY_BYTES) {
          setData({ data: result.data, cachedAt: Date.now() })
        } else {
          console.warn(
            `[SWR Cache] Skipping cache for ${options.baseCacheKey}: ${(
              sizeBytes /
              1024 /
              1024
            ).toFixed(2)}MB exceeds ${MAX_CACHE_ENTRY_BYTES / 1024 / 1024}MB limit`
          )
        }
      }
    }, [setData, result.data, result.error, cacheLoading, isExpired, cachedEntry?.data])

    // If cache is not enabled, return results as-is
    if (cacheOptions?.skipCache) return result as THookResult

    return {
      ...result,
      // This will potentially swallow the error, as long as valid (non-expired) cache exists
      error: hasValidCache ? undefined : result.error,
      errorWhileRevalidating: hasValidCache ? result.error : undefined,
      // Return cached data, if there is data from origin, return that instead
      data: result.data || cachedData,
      // If Apollo already has data, we're done loading. Otherwise, wait for IndexedDB cache or Apollo.
      loading: result.data ? false : cacheLoading || (hasValidCache ? false : result.loading),
    } as THookResult
  }

/**
 * Gets cache keys for the namespace
 */
const getKeys = async ({
  version,
  startsWith,
  inverseSelection,
}: {
  version?: string
  startsWith?: string
  inverseSelection?: boolean
}) => {
  const prefix = [cacheNamespace, version, startsWith].filter(Boolean).join(':')
  const keys = await localforage.keys()

  return (
    keys
      // always get the full namespace, we then filter on top of that
      // this ensures that we only work with keys from this namespace,
      // even when inverting the selection
      .filter(key => key.startsWith(cacheNamespace))
      .filter(key => {
        const matches = key.startsWith(prefix)

        return inverseSelection ? !matches : matches
      })
  )
}

/**
 * Deletes all keys for stale while revalidate cache only, whose version is
 * different to the current one.
 *
 * Is this even really needed? LRU cache ensures that unused keys get deleted eventually, based on their use
 * see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Browser_storage_limits_and_eviction_criteria#lru_policy
 *
 * as such, changing the version would still be enough to invalidate the older cache
 * entries, while eventually deleting them.
 */
export const useQueryStaleWhileRevalidateCleanup = () => {
  useEffect(() => {
    // Delete any keys older than the current cacheVersion
    getKeys({ version: cacheVersion, inverseSelection: true })
      .then(keys => keys.forEach(k => localforage.removeItem(k)))
      .catch(() => console.error('Failed to delete keys'))
  }, [])
}
